import { supabase } from "./supabase";

export interface ProviderOrder {
  order_id: string;
  merchant_id: string;
  merchant_name: string;
  merchant_shop_name: string;
  merchant_shop_domain: string;
  merchant_email: string;
  provider_id: string;
  provider_name: string;
  shopify_order_id: number;
  order_number: string;
  number: number;
  customer_email: string;
  customer_phone: string;
  customer_id: number;
  customer_data: any;
  current_total_price_amount: number;
  current_subtotal_price_amount: number;
  currency_code: string;
  display_financial_status: string;
  display_fulfillment_status: string;
  line_items: any[];
  line_items_count: number;
  shipping_address: any;
  billing_address: any;
  fulfillment_provider: string;
  external_order_id: string;
  fulfillment_tracking_url: string;
  latest_provider_update: {
    status: string;
    tracking_number?: string;
    tracking_url?: string;
    carrier?: string;
    notes?: string;
    updated_at: string;
  } | null;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface ProviderOrderStats {
  total_orders: number;
  pending_orders: number;
  processing_orders: number;
  shipped_orders: number;
  delivered_orders: number;
  total_revenue: number;
  avg_order_value: number;
}

export interface FetchProviderOrdersParams {
  provider_id: string;
  merchant_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface FetchProviderOrdersResponse {
  orders: ProviderOrder[];
  total_count: number;
  stats: ProviderOrderStats;
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface UpdateOrderStatusParams {
  provider_id: string;
  order_id: string;
  fulfillment_status: string;
  tracking_number?: string;
  tracking_url?: string;
  carrier?: string;
  notes?: string;
}

export interface UpdateOrderStatusResponse {
  success: boolean;
  update: {
    id: string;
    order_id: string;
    status: string;
    tracking_number?: string;
    tracking_url?: string;
    carrier?: string;
    updated_at: string;
  };
}

/**
 * Fetch provider orders with optional filters
 */
export async function fetchProviderOrders(
  params: FetchProviderOrdersParams
): Promise<FetchProviderOrdersResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error("Not authenticated");
  }

  const queryParams = new URLSearchParams();
  queryParams.append("provider_id", params.provider_id);

  if (params.merchant_id) queryParams.append("merchant_id", params.merchant_id);
  if (params.status) queryParams.append("status", params.status);
  if (params.date_from) queryParams.append("date_from", params.date_from);
  if (params.date_to) queryParams.append("date_to", params.date_to);
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.offset) queryParams.append("offset", params.offset.toString());

  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/provider-orders?${queryParams}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch provider orders");
  }

  return await response.json();
}

/**
 * Update order fulfillment status
 */
export async function updateOrderStatus(
  params: UpdateOrderStatusParams
): Promise<UpdateOrderStatusResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error("Not authenticated");
  }

  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/provider-orders`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to update order status");
  }

  return await response.json();
}

/**
 * Get order stats for a provider (last 30 days)
 */
export async function getProviderOrderStats(
  provider_id: string
): Promise<ProviderOrderStats> {
  const { data, error } = await supabase.rpc("get_provider_order_stats", {
    p_provider_id: provider_id,
    p_days_back: 30,
  });

  if (error) {
    throw error;
  }

  return data?.[0] || {
    total_orders: 0,
    pending_orders: 0,
    processing_orders: 0,
    shipped_orders: 0,
    delivered_orders: 0,
    total_revenue: 0,
    avg_order_value: 0,
  };
}

/**
 * Format currency amount
 */
export function formatCurrency(amount: number, currencyCode: string = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(amount);
}

/**
 * Format date
 */
export function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateString));
}

/**
 * Get status badge color
 */
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    order_placed: "emerald",
    pending: "yellow",
    processing: "blue",
    in_transit: "indigo",
    shipped: "purple",
    delivered: "green",
    cancelled: "red",
    failed: "red",
    unfulfilled: "orange",
    fulfilled: "green",
  };

  return statusColors[status.toLowerCase()] || "gray";
}
